import {useEffect, useState, VFC} from 'react';
import {useDispatch} from 'react-redux';
import {OrderHistoryItem} from '../components/Products/OrderHistoryItem';
import {useAppSelector} from '../reducks/store/store';
import SimpleDialog, {defaultDialogState, SimpleDialogState} from "../components/Uikit/SimpleDialog";
import {useSearchParams} from 'react-router-dom';
import {CircularProgress, MenuItem, TextField} from "@material-ui/core";
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import './../components/Uikit/custom-datepicker.css'
import Button from "@material-ui/core/Button";
import {selectOrderInfo} from "../reducks/info/selectors";
import {fetchOrderInfo} from "../reducks/info/thunks";
import {Order} from "../reducks/orders/types";
import {
  selectOrderMaxPage,
  selectOrdersByPage,
  selectOrderStatus
} from "../reducks/orders/selectors";
import {cancelOrder, fetchOrders, OrderQueryArgs} from "../reducks/orders/thunks";
import {OrderByDirection} from "firebase/firestore";
import MuiDatePicker from "../components/Uikit/MuiDatePicker";
import {clearFetchedPages, initQueryKey} from "../reducks/orders/slice";
import {toMidnight} from "../utils/dateUtils";

const sortLabels: { sort: OrderByDirection, label: string }[] = [{sort: "desc", label: "新しい順"}, {
  sort: "asc",
  label: "古い順"
}]

const fetchLimit = 5;

/**
 * Dateの月の一日の0:00:00を返す
 * @param date
 */
function getMonthFrom(date: Date) {
  const newDate = new Date(date);
  toMidnight(newDate);
  newDate.setDate(1);
  return newDate;
}

/**
 * Dateの月の最後の日の23:59:59を返す
 * @param date
 */
function getMonthTo(date: Date) {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + 1);
  newDate.setDate(0);
  newDate.setHours(23);
  newDate.setMinutes(59);
  newDate.setSeconds(59);
  return newDate;
}

const paramKeys = {
  page: 'p',
  direction: 'dir',
  from: 'from',
  to: 'to',
}

const OrderHistory: VFC = () => {
  const [dialog, setDialog] = useState<SimpleDialogState>(defaultDialogState);
  const [queryArgs, setQueryArgs] = useState<OrderQueryArgs>({direction: 'desc', from: undefined, to: undefined})
  const [openSortDialog, setOpenSortDialog] = useState(false);
  const [page, setPage] = useState(1);

  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    function strToDate(str: string | null) {
      if (str === null) return undefined;
      return new Date(str);
    }
    const page = Number(params.get(paramKeys.page));
    const _direction = params.get(paramKeys.direction);
    const from = strToDate(params.get(paramKeys.from));
    const to = strToDate(params.get(paramKeys.to));

    setPage(page > 0 ? page : 1);
    setQueryArgs({direction: _direction === 'asc' ? 'asc' : 'desc', from, to})
  }, [params]);

  const orders = useAppSelector((state) => selectOrdersByPage(state, fetchLimit, page, queryArgs));
  const orderInfo = useAppSelector(selectOrderInfo);
  const maxPage = useAppSelector(state => selectOrderMaxPage(state, fetchLimit));
  const orderStatus = useAppSelector(selectOrderStatus);

  // 購入履歴を取得
  useEffect(() => {
    dispatch(fetchOrders({lim: fetchLimit, page, sort: queryArgs}));
  }, [dispatch, queryArgs, page]);

  useEffect(() => {
    if (orderInfo.status === 'idle') {
      dispatch(fetchOrderInfo());
    }
  }, [dispatch, orderInfo]);

  const handleClose = () => setDialog(prev => ({...prev, open: false}));

  const handleCancelOrder = (order: Order) => {
    setDialog({
      open: true,
      title: `注文をキャンセルしますか？`,
      description: `一度キャンセルした注文は元に戻せません`,
      onOk: () => dispatch(cancelOrder({orderId: order.id}))
    })
  }

  const handleSearch = (from: Date | undefined, to: Date | undefined) => {
    const parsedFrom = from ? getMonthFrom(from) : undefined;
    const parsedTo = to ? getMonthTo(to) : undefined;

    setQueryArgs(prev => ({direction: prev.direction, from: parsedFrom, to: parsedTo}));
    setOpenSortDialog(false);
    setParams(prev => {
      function formatDate(date?: Date) {
        // スウェーデン(sv-SE)の日付形式の標準はYYYY-MM-DD
        return date?.toLocaleDateString('sv-SE');
      }
      const fromParam = formatDate(parsedFrom);
      const toParam = formatDate(parsedTo);

      if (fromParam !== undefined) {
        prev.set(paramKeys.from, fromParam);
      } else {
        prev.delete(paramKeys.from);
      }

      if (toParam !== undefined) {
        prev.set(paramKeys.to, toParam);
      } else {
        prev.delete(paramKeys.to);
      }

      return prev;
    });
    setPage(1);
    dispatch(initQueryKey());
    dispatch(fetchOrders({
      lim: fetchLimit,
      page: 1,
      sort: {direction: queryArgs.direction, from: parsedFrom, to: parsedTo},
    }))
  }

  const handleOrderBy = (value: string) => {
    const dir = value as OrderByDirection;
    setQueryArgs(prev => ({...prev, direction: dir}));
    setPage(1);
    setParams(prev => {
      prev.set(paramKeys.page, "1");
      prev.set(paramKeys.direction, dir);
      return prev;
    });
    dispatch(clearFetchedPages());
    dispatch(fetchOrders({
      lim: fetchLimit,
      page: 1,
      sort: {...queryArgs, direction: dir},
    }))
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  };

  const handlePaging = (diff: number) => {
    setPage(prev => prev + diff);
    scrollToTop();
    setParams(prev => {
      prev.set(paramKeys.page, (page + diff).toString());
      return prev;
    });
  }

  return (
    <section className='flex flex-col items-stretch gap-4 px-2'>
      <h2 className='text-pandacco text-2xl mx-auto mt-4 mb-6'>注文履歴</h2>
      <div className={'flex flex-row items-stretch justify-start gap-3 self-stretch'}>
        <TextField label={"並び替え"} select
                   id={"sort-type"} variant={"outlined"} color={"primary"}
                   value={queryArgs.direction}
                   onChange={(e) => handleOrderBy(e.target.value)}>
          {sortLabels.map((v) =>
            <MenuItem key={v.sort} value={v.sort}>
              {v.label}
            </MenuItem>
          )}
        </TextField>
        <div className={"relative flex flex-row items-stretch self-stretch"}>
          <Button variant={"outlined"} color={"primary"} startIcon={<FilterListRoundedIcon/>}
                  onClick={() => setOpenSortDialog(true)}>
            詳細検索
          </Button>
          {openSortDialog &&
              <SortDialog initFrom={queryArgs.from} initTo={queryArgs.to} onSearch={handleSearch}
                          onClose={() => setOpenSortDialog(false)}/>
          }
        </div>
      </div>
      {orders.length > 0 &&
          <>
            {orders.map((order: Order) => (
              <OrderHistoryItem order={order} key={order.id} onCancelOrder={handleCancelOrder}/>
            ))}
              <div className={"flex flex-row gap-4 self-stretch items-center justify-center"}>
                  <Button variant={"outlined"} color={"primary"} disabled={page <= 1}
                          onClick={() => handlePaging(-1)}>
                      前へ
                  </Button>
                  <p className={"text-base"}>
                    {page} / {maxPage}
                  </p>
                  <Button variant={"outlined"} color={"primary"} disabled={maxPage <= page}
                          onClick={() => handlePaging(1)}>
                      次へ
                  </Button>
              </div>
          </>
      }
      {orders.length === 0 &&
          <div className={"flex flex-row justify-center py-2"}>
            {orderStatus === 'idle' || orderStatus === 'loading' ?
              <CircularProgress/>
              :
              <p className='text-gray-500 text-xl'>注文履歴はありません</p>
            }
          </div>
      }
      <SimpleDialog {...dialog} onClose={handleClose}/>
    </section>
  );
};

type DatePickerState = {
  open: boolean,
  value: Date | undefined,
}

const SortDialog = (props: {
  initFrom: Date | undefined,
  initTo: Date | undefined,
  onSearch: (from: Date | undefined, to: Date | undefined) => void,
  onClose: () => void,
}) => {
  const [from, setFrom] = useState<DatePickerState>({open: false, value: props.initFrom});
  const [to, setTo] = useState<DatePickerState>({open: false, value: props.initFrom});

  const open = (type: 'from' | 'to') => {
    const set = type === 'from' ? setFrom : setTo;
    const setOther = type === 'from' ? setTo : setFrom;
    set(prev => ({...prev, open: true}));
    setOther(prev => ({...prev, open: false}));
  }

  useEffect(() => {
    setFrom(prev => ({...prev, value: props.initFrom}));
  }, [props.initFrom])

  useEffect(() => {
    setTo(prev => ({...prev, value: props.initTo}));
  }, [props.initTo])

  const handleSetFrom = (date: Date | null) => {
    setFrom(({open: false, value: date ?? undefined}));

    if (to.value !== undefined && (to.value.getTime() ?? 0) - (date?.getTime() ?? 0) < 0) {
      setTo(({open: false, value: date ?? undefined}));
    }
  }

  const handleSetTo = (date: Date | null) => {
    setTo(({open: false, value: date ?? undefined}));

    if (from.value !== undefined && (from.value.getTime() ?? 0) - (date?.getTime() ?? 0) > 0) {
      setFrom(({open: false, value: date ?? undefined}));
    }
  }

  return <>
    <div className={"fixed top-0 left-0 w-screen h-screen"} onClick={() => props.onClose()}/>
    <div
      className={"absolute top-16 -left-1 z-10 border-material-outline border bg-material-surface rounded-xl flex flex-col items-center"}>
      <div className={"flex flex-row items-start px-2.5 py-2.5 self-stretch bg-material-surfaceVariant"}>
        <p className={"text-sm"}>
          検索条件
        </p>
      </div>
      <hr/>
      <div className={"flex flex-col items-end gap-2 px-1.5 py-2.5 self-stretch"}>
        <MuiDatePicker open={from.open} label={"いつから"} value={from.value}
                       onChange={handleSetFrom}
                       onOpen={() => open('from')}/>
        <MuiDatePicker open={to.open} label={"いつまで"} value={to.value}
                       onChange={handleSetTo}
                       onOpen={() => open('to')}/>
        <Button variant={"contained"} color={"primary"} onClick={() => props.onSearch(from.value, to.value)}
                disabled={props.initFrom?.getTime() === from.value?.getTime() && props.initTo?.getTime() === to.value?.getTime()}>
          検索
        </Button>
      </div>
    </div>
  </>
}

export default OrderHistory;
